import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { PageHead, Badge } from "../../components/ui-bits";
import { toast } from "sonner";
import { Plus, Headset, Ticket, EnvelopeSimple } from "@phosphor-icons/react";

const CATS = ["GST", "ITR", "CMA", "TDS", "Audit", "ROC", "Payroll", "Bookkeeping", "Accounting", "Advisory", "Task"];

export default function AdminAgents() {
  const [agents, setAgents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [ticketFor, setTicketFor] = useState(null);
  const [ticket, setTicket] = useState({ subject: "", category: "Task", description: "" });

  const load = () => api.get("/admin/agents").then((r) => setAgents(r.data));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) { toast.error("Fill all fields"); return; }
    try {
      await api.post("/admin/agents", form);
      toast.success(`Agent ${form.name} created`);
      setForm({ name: "", email: "", password: "" }); setOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not create agent"); }
  };

  const toggleStatus = async (a) => {
    const next = !a.disabled;
    await api.patch(`/admin/users/${encodeURIComponent(a.email)}/status`, { disabled: next });
    toast.success(`${a.name} login ${next ? "disabled" : "enabled"}`);
    load();
  };

  const raiseTicket = async (e) => {
    e.preventDefault();
    if (!ticket.subject) { toast.error("Enter a subject"); return; }
    try {
      await api.post("/admin/agent-ticket", { agent_email: ticketFor.email, ...ticket });
      toast.success(`Task raised to ${ticketFor.name}`);
      setTicketFor(null); setTicket({ subject: "", category: "Task", description: "" });
    } catch (err) { toast.error(err.response?.data?.detail || "Could not raise task"); }
  };

  return (
    <div>
      <PageHead eyebrow="Manage Virtualeena Professionals" title="Agents">
        <button data-testid="new-agent-btn" onClick={() => setOpen(!open)}
          className="bg-[#002FA7] hover:bg-[#00227A] text-white px-4 py-2 rounded-sm text-sm flex items-center gap-2 transition-colors">
          <Plus size={16} /> Add Agent
        </button>
      </PageHead>

      {open && (
        <form onSubmit={create} className="card p-6 mb-6 grid sm:grid-cols-4 gap-3 items-end fade-up">
          <div><label className="label block mb-1">Name</label>
            <input data-testid="agent-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-black/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></div>
          <div><label className="label block mb-1">Email</label>
            <input data-testid="agent-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-black/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></div>
          <div><label className="label block mb-1">Password</label>
            <input data-testid="agent-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full border border-black/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></div>
          <button data-testid="agent-create" className="bg-[#002FA7] hover:bg-[#00227A] text-white px-4 py-2 rounded-sm text-sm transition-colors">Create</button>
        </form>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {agents.map((a, i) => (
          <div key={a.email} data-testid={`agent-card-${i}`} className="card p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 rounded-sm bg-[#002FA7]/10 flex items-center justify-center"><Headset size={22} color="#002FA7" /></div>
              <div className="min-w-0">
                <p className="font-head font-bold flex items-center gap-2">{a.name}
                  {a.disabled && <span className="text-[10px] uppercase font-bold text-[#FF3B30]">Disabled</span>}</p>
                <p className="text-xs text-[#888] truncate">{a.email}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm border-t border-black/10 pt-3 mb-3">
              <div><p className="label mb-0.5">Clients</p><span className="tnum font-semibold">{a.clients}</span></div>
              <div><p className="label mb-0.5">Active Tasks</p><span className="tnum font-semibold">{a.tasks}</span></div>
              <div className="col-span-2"><p className="label mb-0.5">Member Since</p><span className="text-xs">{(a.created_at || "").slice(0, 10) || "—"}</span></div>
            </div>
            <div className="flex items-center justify-between border-t border-black/10 pt-3">
              <div className="flex items-center gap-2">
                <span className="label">Login</span>
                <button data-testid={`agent-toggle-${i}`} onClick={() => toggleStatus(a)} role="switch" aria-checked={!a.disabled}
                  className={`relative w-11 h-6 rounded-full transition-colors ${a.disabled ? "bg-black/15" : "bg-[#00A86B]"}`}>
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${a.disabled ? "" : "translate-x-5"}`} />
                </button>
              </div>
              <button data-testid={`agent-ticket-${i}`} onClick={() => setTicketFor(a)}
                className="text-xs inline-flex items-center gap-1 border border-black/20 hover:bg-black/5 px-3 py-1.5 rounded-sm transition-colors">
                <Ticket size={14} /> Raise Task
              </button>
            </div>
          </div>
        ))}
      </div>

      {ticketFor && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setTicketFor(null)}>
          <form data-testid="agent-ticket-modal" onClick={(e) => e.stopPropagation()} onSubmit={raiseTicket}
            className="bg-white rounded-sm w-full max-w-md p-6 space-y-3">
            <div className="flex items-center gap-2"><Ticket size={20} color="#002FA7" weight="fill" />
              <p className="font-head font-bold text-lg">Raise Task to {ticketFor.name}</p></div>
            <div><label className="label block mb-1">Subject</label>
              <input data-testid="at-subject" value={ticket.subject} onChange={(e) => setTicket({ ...ticket, subject: e.target.value })}
                placeholder="e.g. File GSTR-3B for Acme Traders" className="w-full border border-black/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></div>
            <div><label className="label block mb-1">Category</label>
              <select data-testid="at-ticket-cat" value={ticket.category} onChange={(e) => setTicket({ ...ticket, category: e.target.value })}
                className="w-full border border-black/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
                {CATS.map((c) => <option key={c}>{c}</option>)}
              </select></div>
            <div><label className="label block mb-1">Details</label>
              <textarea data-testid="at-ticket-desc" value={ticket.description} onChange={(e) => setTicket({ ...ticket, description: e.target.value })} rows={3}
                className="w-full border border-black/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></div>
            <div className="flex gap-2 pt-1">
              <button data-testid="at-ticket-submit" className="bg-[#002FA7] hover:bg-[#00227A] text-white px-4 py-2 rounded-sm text-sm inline-flex items-center gap-2 transition-colors"><EnvelopeSimple size={15} /> Send Task</button>
              <button type="button" onClick={() => setTicketFor(null)} className="border border-black/20 px-4 py-2 rounded-sm text-sm hover:bg-black/5">Cancel</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
